import React from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import type { NebulaKpiMetric } from '../../../../types/nebula-insights';
import { ALL_TIME_KPI_NOTE, formatChangePct } from '../utils';

export interface KpiCardConfig {
  key: string;
  label: string;
  metric: NebulaKpiMetric;
  format?: 'integer' | 'decimal';
}

interface KpiCardProps {
  label: string;
  metric: NebulaKpiMetric;
  format?: 'integer' | 'decimal';
  isAllTime?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  metric,
  format = 'integer',
  isAllTime = false,
}) => {
  const displayValue =
    format === 'decimal'
      ? metric.value.toLocaleString(undefined, { maximumFractionDigits: 1 })
      : metric.value.toLocaleString();

  const changeLabel = formatChangePct(metric.changePct);
  const isPositive = (metric.changePct ?? 0) >= 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 h-full">
      <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
        {label}
      </p>
      <p className="text-2xl sm:text-[1.875rem] font-semibold text-gray-900 font-family-messina-sans mt-2 tabular-nums">
        {displayValue}
      </p>
      {changeLabel ? (
        <p
          className={`text-xs font-family-messina-sans mt-2 flex items-center gap-1 ${
            isPositive ? 'text-emerald-600' : 'text-amber-600'
          }`}
        >
          {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {changeLabel}
        </p>
      ) : isAllTime ? (
        <p className="text-xs text-gray-500 font-family-messina-sans mt-2">
          {ALL_TIME_KPI_NOTE}
        </p>
      ) : null}
    </div>
  );
};

interface KpiCardsRowProps {
  items: KpiCardConfig[];
  isAllTime?: boolean;
}

const KpiCardsRow: React.FC<KpiCardsRowProps> = ({ items, isAllTime = false }) => (
  <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
    {items.map((item) => (
      <KpiCard
        key={item.key}
        label={item.label}
        metric={item.metric}
        format={item.format}
        isAllTime={isAllTime}
      />
    ))}
  </div>
);

export default KpiCardsRow;
